import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { TStoreInventory } from 'shared/types/cart';
import Button from 'components/commercetools-ui/atoms/button';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import aggregatedQuantity from 'helpers/utils/aggregatedQuantity';
import { Guid } from 'helpers/utils/guid';
import { sdk } from 'sdk';
import { useAmazonPay, useCart } from 'frontastic';
import useGivex from 'frontastic/hooks/useGivex';
import AmazonReviewComponent from './components/amazonpay-preview';
import AmazonpayReviewPageSkeleton from './components/amazonpay-review-skeleton';
import AmazonSessionError from './components/amazonpay-session-error';
import { AmazonReviewData } from './types/AmazonReviewData';
import Header, { Props as HeaderProps } from '../checkout//components/header';
import Secure from '../checkout/components/secure';
import OrderSummary from '../order-summary';

export type CheckoutWrappedProps = HeaderProps & {
  amazonCheckoutSessionId: string;
};

const AmazonPayCheckout: React.FC<CheckoutWrappedProps> = ({ logo, amazonCheckoutSessionId, ...emptyState }) => {
  const { getCountry } = useLocale();
  const { getAmazonCheckoutSessionData } = useAmazonPay();
  const { data, productSKUs, transaction } = useCart();
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const [isReadyForPurchase, setIsReadyForPurchase] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [isSessionDataLoaded, SetSessionDataLoaded] = useState(false);
  const [processedCompletePayment, SetProcessedCompletePayment] = useState(false);
  const [isSessionDataInvalid, SetIsSessionDataInvalid] = useState(false);
  const [isPaymentError, SetIsPaymentError] = useState({
    result: false,
    message: '',
  });
  const [amazonCheckoutSessionData, setAmazonCheckoutSessionData] = useState<AmazonReviewData | null>(null);
  const router = useRouter();
  const cartAmount = CurrencyHelpers.formatMoney(transaction.total);
  const searchParams = useSearchParams();
  const completePaymentParam = searchParams.get('completePayment');
  const { placeOrder, preauthGiftcards, cancelPreauthGiftcards } = useGivex();

  useEffect(() => {
    if (amazonCheckoutSessionId && !completePaymentParam) {
      fetchData(amazonCheckoutSessionId);
    }
  }, []);

  useEffect(() => {
    const completeAmazonCheckout = async () => {
      try {
        SetProcessedCompletePayment(true);
        if (containSplitGivexPayments) {
          await preauthGiftcards();
        }
        const res = await sdk.composableCommerce.amazon.completeAmazonCheckout({
          checkoutSessionId: amazonCheckoutSessionId,
          cartId: data?.cartId || '',
          payload: {
            chargeAmount: {
              amount: `${cartAmount}`,
              currencyCode: transaction.total.currencyCode,
            },
          },
        });
        if (!res?.isError) {
          sessionStorage.setItem('pushToDataLayer', 'true');
          router.push(`/thank-you?orderId=${res.data.orderNumber}`);
        } else {
          if (containSplitGivexPayments) {
            await cancelPreauthGiftcards();
          }
          SetIsPaymentError({
            result: true,
            message: formatCheckoutMessage({
              id: 'amazon.pay.order.creation.failed',
              defaultMessage:
                'Unfortunately, payment has not been authorised. Please update payment method to continue.',
            }),
          });
          setProcessing(false);
          fetchData(amazonCheckoutSessionId);
        }
      } catch (e) {
        if (containSplitGivexPayments) {
          await cancelPreauthGiftcards();
        }
        SetIsPaymentError({
          result: true,
          message: formatCheckoutMessage({
            id: 'amazon.pay.order.creation.failed',
            defaultMessage: 'Unfortunately, payment has not been authorised. Please update payment method to continue.',
          }),
        });
        setProcessing(false);
      }
    };
    if (completePaymentParam && !processedCompletePayment && data?.cartId) {
      cartItemsValid ? completeAmazonCheckout() : router.push('/cart');
    }
  }, [completePaymentParam, data]);

  const fetchData = async (sessionId: string) => {
    getAmazonCheckoutSessionData(sessionId)
      .then(async (response) => {
        const sessionData: AmazonReviewData = response as AmazonReviewData;
        setAmazonCheckoutSessionData(sessionData);
        if (sessionData.email == undefined || sessionData.email == '') {
          SetIsSessionDataInvalid(true);
        }
        SetSessionDataLoaded(true);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        SetSessionDataLoaded(true);
        SetIsSessionDataInvalid(true);
      });
  };

  const enablePurchaseButton = (flag: boolean) => {
    setIsReadyForPurchase(flag);
  };

  const hasGiftcardItem = useMemo(() => {
    return data?.lineItems?.some((item) => item.isGiftcard === true) || false;
  }, [data?.lineItems]);

  const cartItemsValid = useMemo(
    () => !((data && data.lineItems?.length === 0) || hasGiftcardItem),
    [data, hasGiftcardItem],
  );

  const areAllGivexPayments = useMemo(
    () => data?.sum?.centAmount === 0 && data?.payments && data?.payments?.length > 0,
    [data?.sum, data?.payments],
  );

  const containSplitGivexPayments = useMemo(() => {
    const hasGivexPayments = data?.payments && data?.payments.some((payment) => payment.giftcardNumber);
    return data?.sum?.centAmount && data?.sum?.centAmount > 0 && hasGivexPayments;
  }, [data?.sum, data?.payments]);

  const initiateGivexPayment = async () => {
    placeOrder()
      .then(async (response) => {
        const orderNumber = response.orderNumber;
        //await sdk.callAction({ actionName: 'cart/resetCart' });
        sessionStorage.setItem('pushToDataLayer', 'true');
        router.push(`/thank-you?orderId=${orderNumber}`);
      })
      .catch((error) => {
        SetIsPaymentError({
          result: true,
          message: formatCheckoutMessage({
            id: 'amazon.pay.order.creation.failed',
            defaultMessage: 'Unfortunately, payment has not been authorised. Please update payment method to continue.',
          }),
        });
        setProcessing(false);
      });
  };
  const checkStockAndProceed = async () => {
    try {
      SetIsPaymentError({
        result: false,
        message: '',
      });
      const fluentStock = await checkStock(productSKUs as string[], getCountry());
      const hasNoStockItems = hasUnavailableItems(fluentStock);
      if (hasNoStockItems) {
        router.push('/cart');
      } else {
        continuePayment();
      }
    } catch (e) {
      SetIsPaymentError({
        result: true,
        message: formatCheckoutMessage({
          id: 'amazon.pay.insufficient.failed',
          defaultMessage:
            "Unfortunately, your cart includes items which can't be fulfilled at this time.  Please remove these items from your cart to continue.",
        }),
      });
      setProcessing(false);
    }
  };
  const continuePayment = async () => {
    if (areAllGivexPayments) {
      initiateGivexPayment();
    } else {
      updateCheckoutSession(true);
    }
  };

  const checkStock = useCallback(async (productSKUs: string[], countryCode: string) => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.stock.getProductInventory({ productSKUs, countryCode });
    if (!res.isError) {
      return res.data as TStoreInventory[];
    }
    return [];
  }, []);

  const hasUnavailableItems = (fluentStocks: TStoreInventory[]) => {
    return data?.lineItems?.some((item) => {
      if (item?.isPerpetualStock) {
        return;
      }
      const availableQuantity = aggregatedQuantity(item.variant?.sku, fluentStocks);
      const hasInsufficientStocks = item.count && item.count > availableQuantity;
      const hasOutOfStockItems = availableQuantity === 0;
      return hasOutOfStockItems || hasInsufficientStocks;
    });
  };

  const updateCheckoutSession = async (redirect: boolean) => {
    const orderNumber = Guid.newGuid(false, ['', 'xxxx-xxxx-yxxx']);
    const returnUrl = window.location.origin + '/en/checkout/?completePayment=true';
    try {
      const resultObj: any = await sdk.composableCommerce.amazon.updateAmazonCheckoutSession({
        checkoutSessionId: amazonCheckoutSessionId,
        payload: {
          webCheckoutDetails: {
            checkoutResultReturnUrl: returnUrl,
          },
          paymentDetails: {
            paymentIntent: 'AuthorizeWithCapture',
            canHandlePendingAuthorization: false,
            chargeAmount: {
              amount: `${cartAmount}`,
              currencyCode: transaction.total.currencyCode,
            },
            softDescriptor: '',
          },
          merchantMetadata: {
            merchantReferenceId: orderNumber,
            merchantStoreName: 'American Golf online',
            noteToBuyer: `${data?.cartId}`,
            customInformation: '',
          },
        },
      });
      if (resultObj?.data == undefined) {
        SetIsPaymentError({
          result: true,
          message: formatCheckoutMessage({
            id: 'amazon.pay.order.creation.failed',
            defaultMessage: 'Unfortunately, payment has not been authorised. Please update payment method to continue.',
          }),
        });
        setProcessing(false);
      } else {
        const redirectURL = resultObj?.data?.webCheckoutDetails?.amazonPayRedirectUrl;
        if (redirect && redirectURL) {
          window.location.replace(redirectURL);
        }
      }
    } catch (e) {
      SetIsPaymentError({
        result: true,
        message: formatCheckoutMessage({
          id: 'amazon.pay.order.creation.failed',
          defaultMessage: 'Unfortunately, payment has not been authorised. Please update payment method to continue.',
        }),
      });
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen pb-48 lg:bg-neutral-200">
      <div className="lg:mx-[48px]">
        <Secure />
        {!amazonCheckoutSessionData && !isSessionDataInvalid && !isPaymentError.result && (
          <>
            <AmazonpayReviewPageSkeleton />
          </>
        )}
        {amazonCheckoutSessionData && (
          <div className="flex-row-reverse items-start gap-24 lg:flex">
            <OrderSummary
              className="bg-white px-16 md:px-24 lg:w-[30%] lg:rounded-md lg:p-36 lg:px-20 xl:px-48"
              includeSummaryAccordion
              title={formatCartMessage({ id: 'order.summary', defaultMessage: 'Order summary' })}
              classNames={{
                applyDiscountButton: 'lg:mb-0 py-14 text-16 mb-16 border-b border-neutral-400 lg:border-b-transparent',
                totalAmount: 'text-18 md:pb-20',
                subCostsContainer: 'pt-12 md:pt-16 mb-20 lg:py-24 lg:mb-16 lg:border-b border-neutral-400',
              }}
              button={
                <Button
                  variant="primary"
                  disabled={!isReadyForPurchase}
                  className="w-full uppercase"
                  type="submit"
                  onClick={() => {
                    setProcessing(true);
                    checkStockAndProceed();
                  }}
                  loading={processing}
                  size="m"
                >
                  {formatCheckoutMessage({ id: 'complete.purchase', defaultMessage: 'Complete purchase' })}
                </Button>
              }
            />
            {!isSessionDataInvalid && (
              <>
                <AmazonReviewComponent
                  enablePurchaseButton={enablePurchaseButton}
                  amazonCheckoutSessionId={amazonCheckoutSessionId}
                  amazonCheckoutSessionData={amazonCheckoutSessionData}
                  paymentError={isPaymentError}
                />
                <div className="mt-24 px-16 md:px-24 lg:hidden">
                  <Button
                    variant="primary"
                    disabled={!isReadyForPurchase}
                    className="w-full uppercase"
                    type="submit"
                    onClick={() => {
                      setProcessing(true);
                      checkStockAndProceed();
                    }}
                    loading={processing}
                    size="m"
                  >
                    {formatCheckoutMessage({ id: 'complete.purchase', defaultMessage: 'Complete purchase' })}
                  </Button>
                </div>
              </>
            )}
            {isSessionDataInvalid && (
              <AmazonSessionError
                errorText={formatCheckoutMessage({
                  id: 'payment.failed',
                  defaultMessage: 'We could not process your payment, please try again later',
                })}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AmazonPayCheckout;
